// @ts-strict-ignore
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ElementRef, Injectable } from '@angular/core';
import { ConnectedOverlayScrollHandler } from 'primeng/dom';
import { TooltipHintComponent } from './tooltip-hint/tooltip-hint.component';
import { DEFAULT_TOOLTIP_CONFIG, TooltipConfig } from './types';

@Injectable({
  providedIn: 'root',
})
export class TooltipService {
  isVisible: boolean = false;

  private _config: TooltipConfig;
  private _elementRef: ElementRef<HTMLElement>;
  private _overlayRef: OverlayRef;
  private _scrollHandler: ConnectedOverlayScrollHandler;

  readonly MARGIN = 10;

  constructor(private _overlay: Overlay) {
    this._overlayRef = this._overlay.create();
  }

  hide() {
    this._overlayRef.detach();
    this._scrollHandler?.unbindScrollListener();
    this.isVisible = false;
  }

  show(elementRef: ElementRef<HTMLElement>, config?: TooltipConfig) {
    this._teardown();
    if (!!config) {
      this._setup(elementRef, {
        ...DEFAULT_TOOLTIP_CONFIG,
        ...config,
      });
      this._updateOverlayStyle();
    }
    this._updateOverlayStyle();
    this.isVisible = true;
  }

  private _getOverlayStyle() {
    const elementBounds = this._elementRef?.nativeElement?.getBoundingClientRect();

    if (!elementBounds) {
      return undefined;
    }

    switch (this._config.position) {
      case 'top': {
        return {
          top: `${window.scrollY + elementBounds.y - this.MARGIN}px`,
          left: `${window.scrollX + elementBounds.x + elementBounds.width / 2}px`,
          justifyContent: 'center',
          transform: 'translateY(-100%) translateX(-50%)',
        };
      }

      case 'bottom': {
        return {
          top: `${window.scrollY + elementBounds.y + elementBounds.height + this.MARGIN}px`,
          left: `${window.scrollX + elementBounds.x + elementBounds.width / 2}px`,
          justifyContent: 'center',
          transform: 'translateX(-50%)',
        };
      }

      case 'left': {
        return {
          top: `${window.scrollY + elementBounds.y + elementBounds.height / 2}px`,
          left: `${window.scrollX + elementBounds.x - this.MARGIN}px`,
          justifyContent: 'flex-end',
          transform: 'translateX(-100%) translateY(-50%)',
        };
      }

      case 'right': {
        return {
          top: `${window.scrollY + elementBounds.y + elementBounds.height / 2}px`,
          left: `${window.scrollX + elementBounds.x + elementBounds.width + this.MARGIN}px`,
          justifyContent: 'flex-start',
          transform: 'translateY(-50%)',
        };
      }
    }
  }

  private _setup(elementRef: ElementRef<HTMLElement>, config: TooltipConfig) {
    this._elementRef = elementRef;
    this._config = config;

    const tooltipPortal = new ComponentPortal(TooltipHintComponent);
    const tooltipRef = this._overlayRef.attach(tooltipPortal);
    tooltipRef.instance.config = config;

    this._scrollHandler = new ConnectedOverlayScrollHandler(
      elementRef.nativeElement,
      this._updateOverlayStyle.bind(this)
    );
    this._scrollHandler.bindScrollListener();
  }

  private _teardown() {
    this._elementRef = undefined;
    this._config = undefined;
    this._overlayRef.detach();
    this._scrollHandler?.unbindScrollListener();
  }

  private _updateOverlayStyle() {
    const overlayStyle = this._getOverlayStyle();
    Object.assign(this._overlayRef.overlayElement.style, overlayStyle);
  }
}
